import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { Alert, AppBar, Box, Button, ButtonGroup, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Tab, Typography } from "@mui/material";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { Localized } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectIsTokenValid } from "../../user/userSlice";
import { APIError } from "../../../app/appTypes";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ServerErrorMsg } from "../../../app/ServerErrorMsg";
import './importedDocument.css';
import { ImportedDocument, loadImportedDocumentById, loadImportedDocumentSuggestions, runImportedDocumentAiAnalysis } from "./importedDocumentsApi";
import { selectAllElements, selectImportedDocActions, selectImportedDocument, selectImportedDocWorkflowActions, setImportedDocumentActions } from "./importedDocSlice";
import PrintIcon from "@mui/icons-material/Print";
import { ImportedDocumentHeader } from "./components/ImportedDocumentHeader";
import { loadUrsOfferNotes, saveAllPendingNotes, selectAllUrsOfferNotes, selectPendingChanges } from "../../ursOffer/ursOfferNotesSlice"
import { loadAllDepartmentAssignements, saveDepartmentAssignments, selectHasPendingDeptChanges } from "./importedDocDepartmentsSlice";
import { loadAllDocLinks, saveDocLinks, selectHasPendingDeptChanges as selectHasPendingDocLinkChanges } from "./importedDocLinksSlice";
import { loadAllHiddenParts, saveHiddenParts, selectHasPendingHiddenParts } from "./importedDocHiddenPartsSlice";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { Attachements } from "../../urs/Attachements";
import { BlobResourceType } from "../blobResourceTypes";
import { loadManyBlobs } from "../attachementsSlice";
import { TeamMemberView } from "../components/TeamMemberView";
import { BackgroundProcessLauncher } from "../../backgroundProcessing/BackgroundProcessLauncher";
import { DocumentActions, DocumentWorkflowActions, loadDocumentActions } from "../documentsApi";
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { ImportedDocumentDetailsPanel } from "./ImportedDocumentDetailsPanel";
import { DocumentSoftDeleteDialog } from "../components/DocumentSoftDeleteDialog";
import { DocumentReportPrinter } from "../components/DocumentReportPrinter";
import { ActionTitle } from "../components/WorkflowActionUtils";
import { WorkflowActionRunner } from "../components/WorkflowActionRunner";
import { ImportedDocumentTeamMemberView } from "./components/ImportedDocumentTeamMemberView";
import { ErrorBoundary } from "../../../app/ErrorBoundary";
import { DocumentExcelExporter } from "../components/DocumentExcelExporter";
import { GreenExcelIcon } from "../../../app/customIcons/ExcelIcon";
import { selectSettings } from "../../settings/settingsSlice";

type ImportedDocumentTabs = "content" | "details" | "team" | "private-attachments"
type ImportedDocumentReportType = "side" | "compact" | "clean"

// Legacy (JS-measured) classic renderer — RETAINED, not deleted. The static
// renderer is now the default and the user-facing Classic/Static toggle is
// gone; flip USE_LEGACY_CLASSIC_RENDERER to remount the classic path.
const DocumentWithReviewPanel = lazy(() => import('./HtmlWithAnchors'))
const StaticDocumentReview = lazy(() => import('./ast/StaticDocumentReview'))

const USE_LEGACY_CLASSIC_RENDERER = false

export const ImportedDocumentForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { id: paramId } = useParams();
    const id: number = parseInt(paramId ?? "0");

    const { doc } = useAppSelector(selectImportedDocument)
    const actions = useAppSelector(selectImportedDocActions);
    const allWorkflowActions = useAppSelector(selectImportedDocWorkflowActions);
    const [tab, setTab] = useState<ImportedDocumentTabs>("content");

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [error, setError] = useState<any>(null);
    const [isAiRunning, setIsAiRunning] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [printReportType, setPrintReportType] = useState<ImportedDocumentReportType>("side");
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
    const [exportToExcel, setExportToExcel] = useState(false);
    const { locale } = useAppSelector(selectSettings);
    const [aiServerError, setAiServerError] = useState<APIError | null>(null);
    const [selectedAction, setSelectedAction] = useState<DocumentWorkflowActions | null>(null);

    const pendingChanges = useAppSelector(selectPendingChanges);
    const hasPendingDepts = useAppSelector(selectHasPendingDeptChanges);
    const hasPendingDocLinks = useAppSelector(selectHasPendingDocLinkChanges);
    const hasPendingHiddenParts = useAppSelector(selectHasPendingHiddenParts);
    const hasPending = !isDeleted && (Object.keys(pendingChanges).length > 0 || hasPendingDepts || hasPendingDocLinks || hasPendingHiddenParts);

    // The document-actions endpoint drives edit permission. Without an "Edit"
    // content action the backend rejects saves, so the whole review surface is
    // read-only: no Save (a clear notice instead of a failing button), and the
    // NotePanel's inputs are disabled. Search / filtering stay available to all.
    const canEdit = actions.includes("Edit");
    const canDelete = actions.includes("DeleteDocument");

    // The session-expiry redirect (Authenticate -> navigate('/login')) is an in-app
    // navigation, so an unconditional blocker swallows it and strands the user on a
    // dead page with no retry. Once the token is invalid the pending edits cannot be
    // saved anyway, so stop blocking and let the redirect through.
    const tokenValid = useAppSelector(selectIsTokenValid);
    const shouldBlock = hasPending && tokenValid;
    const blocker = useBlocker(shouldBlock);

    useEffect(() => {
        if (!hasPending) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasPending]);

    // Flipping shouldBlock does not release an already-blocked blocker, so if the
    // session died while the confirm dialog was open we have to let it through here.
    useEffect(() => {
        if (!tokenValid && blocker.state === 'blocked') blocker.proceed?.();
    }, [tokenValid, blocker]);

    const handleSaveClick = async () => {
        setIsSaving(true);
        try {
            await Promise.all([
                dispatch(saveAllPendingNotes(id)).unwrap(),
                saveDepartmentAssignments(id, dispatch),
                saveDocLinks(id, dispatch),
                dispatch(saveHiddenParts(id)).unwrap(),
            ]);
            dispatch(showSuccess('saved'));
        } catch {
            dispatch(showError('error'));
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrintClick = (reportType: ImportedDocumentReportType = "side") => {
        setPrintReportType(reportType);
        setIsPrintDialogOpen(true);
    };

    const handlePrintMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setPrintMenuAnchorEl(event.currentTarget);
    };

    const handlePrintMenuClose = () => {
        setPrintMenuAnchorEl(null);
    };

    const handlePrintOptionClick = (reportType: ImportedDocumentReportType) => {
        handlePrintMenuClose();
        handlePrintClick(reportType);
    };

    const handleExportToExcelClick = () => {
        handlePrintMenuClose();
        setExportToExcel(true);
    };

    const handleDeleteSuccess = useCallback(() => {
        setIsDeleted(true);
        navigate('/imported-documents');
    }, [navigate]);

    // In-flight guard: coalesce concurrent load() calls for the same document so
    // the mount effect and a background-process completion refresh can't trigger
    // two overlapping load cascades (see doc/background-processing-architecture.md §3.3).
    const loadInFlight = useRef<{ id: number; promise: Promise<void> } | null>(null);

    const load = useCallback(async () => {
        if (!id) return;

        if (loadInFlight.current?.id === id) {
            return loadInFlight.current.promise;
        }

        const run = (async () => {
            setIsLoading(true);
            setIsError(false);
            setError(null);

            try {
                await dispatch(loadImportedDocumentById(id)).unwrap()
                await dispatch(loadDocumentActions(id.toString())).unwrap()
                await dispatch(loadImportedDocumentSuggestions(id)).unwrap()

                await dispatch(loadUrsOfferNotes({ docId: id, }))
                await dispatch(loadAllDepartmentAssignements({ docId: id }))
                await dispatch(loadAllDocLinks({ docId: id }))
                await dispatch(loadAllHiddenParts({ docId: id }))

            } catch (err) {
                console.error(err);
                setIsError(true);
                setError(err);

            } finally {
                setIsLoading(false)
                if (loadInFlight.current?.id === id) {
                    loadInFlight.current = null;
                }
            }
        })();

        loadInFlight.current = { id, promise: run };
        return run;
    }, [dispatch, id]);

    const handleBackgroundProcessCompleted = useCallback(async () => {
        // The completion toast is shown by backgroundCompletionListener; here we
        // only refresh the on-screen data.
        try {
            await load();
        } catch {
            dispatch(showError('error'));
        }
    }, [dispatch, load]);


    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!doc || !doc.guid) return;

        dispatch(loadManyBlobs([doc.guid])).unwrap();
    }, [dispatch, id, doc?.guid, doc?.id]);

    const handleTabChange = useCallback((_: any, newTab: ImportedDocumentTabs) => {
        setTab(newTab);
    }, []);

    const handleConfirmRunAiClick = useCallback(async () => {
        if (!id) return false;

        try {
            setIsAiRunning(true);
            setAiServerError(null);

            const result = await dispatch(runImportedDocumentAiAnalysis(id));

            if (runImportedDocumentAiAnalysis.fulfilled.match(result)) {
                return true;
            } else if (result.payload && typeof result.payload === 'object' && 'kind' in result.payload) {
                setAiServerError(result.payload as APIError);
            } else {
                dispatch(showError('error'));
            }
        } catch {
            dispatch(showError('error'));
        } finally {
            setIsAiRunning(false);
        }
        return false;
    }, [dispatch, id]);

    const handleActionClick = (action: DocumentWorkflowActions) => {
        setSelectedAction(action);
    }
    const onWorkflowActionSuccess = async (documentActions: DocumentActions) => {
        dispatch(setImportedDocumentActions(documentActions))
        setSelectedAction(null)

        try {
            await load()
        } catch {
            dispatch(showError('error'))
        }
    }

    if (!id) {
        return <ErrorContainer error={{ message: "Brak parametru id w URL." }} />;
    }

    if (isError) {
        return <ErrorContainer error={error} />;
    }

    // Only show the full-screen loader on the initial load or when navigating to
    // a different document — NOT on a same-document background refresh. Unmounting
    // the form on every refresh would destroy the BackgroundProcessLauncher's
    // completion guard, causing a finished process to re-trigger load() on every
    // remount (infinite reload loop).
    if (!doc || doc.id !== id) {
        return <LoadingContainer />;
    }

    return (
        <Container
            maxWidth={false}
            sx={{ padding: "2px", paddingLeft: "2px!important", paddingRight: "2px!important" }}
        >
            <ImportedDocumentHeader document={doc} />
            <Grid container>
                <Grid item xs={12}>
                    <TabContext value={tab}>
                        <TabList onChange={handleTabChange}>
                            <Tab
                                key={0}
                                value="content"
                                label={
                                    <span>
                                        <Localized id="content">Content</Localized> ({doc.elements?.length ?? 0})
                                    </span>
                                }

                            />
                            <Tab
                                key={1}
                                value="details"
                                label={<Localized id="document-details">_details</Localized>} />
                            <Tab
                                key={2}
                                value="team"
                                label={<Localized id="team">Team</Localized>} />
                            <Tab
                                key={3}
                                value="private-attachments"
                                label={<Localized id="">Private attachmets</Localized>} />
                        </TabList>
                        <TabPanel value="content" sx={{ p: 0, pt: 1 }}>
                            {/* The document is imported supplier HTML we don't control, so a
                                render error here is a question of when, not if. Bounded at the
                                panel: the header, the other tabs and Save keep working, and a
                                reviewer with unsaved notes doesn't lose them. */}
                            <ErrorBoundary resetKeys={[doc.id]}>
                                <ImportedDocView doc={doc} readOnly={!canEdit} />
                            </ErrorBoundary>
                        </TabPanel>
                        <TabPanel value="team">
                            <ImportedDocumentTeamMemberView
                                documentId={doc.id.toString()}
                                discussionRelatedObjectId={doc.guid}
                                actions={actions}
                            />
                        </TabPanel>
                        <TabPanel value={"details"}>
                            <ImportedDocumentDetailsPanel document={doc} />
                        </TabPanel>
                        <TabPanel value="private-attachments">
                            <Attachements resourceId={doc.guid} resourceType={BlobResourceType.Document} canDelete={true} canUpload={true} />
                        </TabPanel>
                    </TabContext>
                </Grid>
            </Grid>

            <AppBar
                position="fixed"
                color="primary"
                sx={{
                    top: "auto",
                    bottom: 0,
                    p: 0.75,
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 1 }}
                >
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        <Stack spacing={2} direction="row">
                            {allWorkflowActions.map((action) => (
                                <LoadingButton
                                    key={action}
                                    size="small"
                                    variant="contained"
                                    color={"info"}
                                    onClick={() => handleActionClick(action)}
                                >
                                    <ActionTitle action={action} />
                                </LoadingButton>
                            ))}
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={4} alignItems="center">
                        <Stack direction="row" spacing={1}>
                            {canEdit ? (
                                <LoadingButton
                                    variant="contained"
                                    size="small"
                                    loading={isSaving}
                                    disabled={!hasPending}
                                    color='info'
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveClick}
                                >
                                    <Localized id="save">Save</Localized>
                                </LoadingButton>
                            ) : (
                                <Alert severity="info" sx={{ py: 0, alignItems: "center" }}>
                                    <Localized id="imported-doc-no-edit-permission">
                                        <span>You don't have permission to edit this document</span>
                                    </Localized>
                                </Alert>
                            )}

                            <BackgroundProcessLauncher
                                triggerLabel="Run AI"
                                triggerDisabled={!canEdit}
                                triggerButtonProps={{
                                    variant: "contained",
                                    size: "small",
                                }}
                                questionTitle="Run AI pre-analysis"
                                questionDescription={
                                    <>
                                        AI will scan the document and automatically mark clearly non-requirement content as <b>Not a requirement</b>.
                                    </>
                                }
                                trackingLabel="Run AI pre-analysis"
                                questionChildren={<ServerErrorMsg err={aiServerError} />}
                                loading={isAiRunning}
                                objectType="IMPORTED_DOCUMENT"
                                objectId={id.toString()}
                                onStart={handleConfirmRunAiClick}
                                onCompleted={handleBackgroundProcessCompleted}
                                onOpenQuestion={() => setAiServerError(null)}
                                onCloseQuestion={() => setAiServerError(null)}
                            />

                            <ButtonGroup variant="contained" size="small">
                                <Button
                                    size="small"
                                    startIcon={<PrintIcon />}
                                    onClick={() => handlePrintClick("side")}
                                >
                                    <Localized id="urs-offer-action-print">Wydruk</Localized>
                                </Button>
                                <Button
                                    size="small"
                                    aria-haspopup="menu"
                                    aria-expanded={printMenuAnchorEl ? 'true' : undefined}
                                    aria-controls={printMenuAnchorEl ? 'imported-document-print-menu' : undefined}
                                    onClick={handlePrintMenuOpen}
                                    sx={{ minWidth: 36, px: 0.5 }}
                                >
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<GreenExcelIcon />}
                                onClick={handleExportToExcelClick}
                                sx={{ '& .MuiButton-startIcon svg': { width: 20, height: 20 } }}
                            >
                                <Localized id="imported-doc-excel-print">_Wydruk Excel</Localized>
                            </Button>

                            <Menu
                                id="imported-document-print-menu"
                                anchorEl={printMenuAnchorEl}
                                open={Boolean(printMenuAnchorEl)}
                                onClose={handlePrintMenuClose}
                            >
                                <MenuItem onClick={() => handlePrintOptionClick("side")}>
                                    <ListItemIcon>
                                        <PictureAsPdfOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Side by side report</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => handlePrintOptionClick("compact")}>
                                    <ListItemIcon>
                                        <PictureAsPdfOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Compact report</ListItemText>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => handlePrintOptionClick("clean")}>
                                    <ListItemIcon>
                                        <PictureAsPdfOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>Clean report</ListItemText>
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={handleExportToExcelClick}>
                                    <ListItemIcon sx={{ minWidth: 36, '& svg': { width: 20, height: 20 } }}>
                                        <GreenExcelIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={<Localized id="export-to-excel">Export to excel</Localized>} />
                                </MenuItem>
                            </Menu>
                        </Stack>
                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{
                                mx: 1,
                                borderColor: 'rgba(255, 255, 255, 0.35)'
                            }}
                        />

                        <Stack direction="row" spacing={1}>
                            {canDelete && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setDeleteDialogOpen(true)}
                                >
                                    <Localized id="delete">Usuń</Localized>
                                </Button>
                            )}

                            <Button
                                onClick={() => navigate(-1)}
                                variant="contained"
                                size="small"
                            >
                                <Localized id="back">Wróć</Localized>
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </AppBar>
            {
                selectedAction && (
                    <WorkflowActionRunner
                        documentId={doc.id.toString()}
                        action={selectedAction}
                        onSuccess={onWorkflowActionSuccess}
                        onCancel={() => setSelectedAction(null)} />)
            }
            {exportToExcel && <DocumentExcelExporter
                target="imported-document"
                documentId={doc.id.toString()}
                language={locale}
                mode="download"
                isDialogOpen={true}
                closeDialog={() => setExportToExcel(false)}
            />}
            <DocumentSoftDeleteDialog
                open={deleteDialogOpen}
                documentId={String(doc.id)}
                documentNo={doc.number}
                onClose={() => setDeleteDialogOpen(false)}
                onSuccess={handleDeleteSuccess}
            />
            <DocumentReportPrinter
                documentId={String(doc.id)}
                language="en"
                mode="download"
                reportPath={`api/reports/supplier-imported-document/${printReportType}/${doc.id}`}
                isDialogOpen={isPrintDialogOpen}
                closeDialog={() => setIsPrintDialogOpen(false)}
            />
            <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
                <DialogTitle>
                    <Localized id="unsaved-data">Unsaved data</Localized>
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        <Localized id="unsaved-data-will-be-lost">
                            Unsaved data will be lost. Continue?
                        </Localized>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => blocker.reset?.()}>
                        <Localized id="cancel">Cancel</Localized>
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={() => blocker.proceed?.()}
                    >
                        <Localized id="leave-without-saving">Leave without saving</Localized>
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const ImportedDocView = (props: { doc: ImportedDocument; readOnly?: boolean }) => {
    const elements = useAppSelector(selectAllElements)
    const notes = useAppSelector(selectAllUrsOfferNotes)

    const counts: Record<string, number> = useMemo(() => {
        return notes.reduce((acc, n) => {
            return {
                ...acc,
                [`${n.blockKey}`]: 1,
            }
        }, {})
    }, [notes])

    return (
        <div className="imported-document">
            <Suspense fallback={<Paper sx={{
                p: 2,
            }}>
                <Typography fontFamily='monospace'>Loading resources... Please wait.</Typography>
            </Paper>}>
                {USE_LEGACY_CLASSIC_RENDERER ? (
                    <DocumentWithReviewPanel
                        docId={props.doc.id}
                        elements={elements}
                        commentCounts={counts}
                        onSaveReview={(payload) => console.log("SAVE:", payload)}
                    />
                ) : (
                    <StaticDocumentReview
                        docId={props.doc.id}
                        docGuid={props.doc.guid}
                        elements={elements}
                        readOnly={props.readOnly}
                    />
                )}
            </Suspense>
        </div>
    );
};
